/**
 * Трекер — PWA: сервис-воркер (src/sw.ts) отдаёт index.html и бандл из офлайн-кэша. Простой reload после
 * выкладки вернул бы ту же старую сборку из кэша. Поэтому перед перезагрузкой новый воркер должен
 * скачаться и взять управление вкладкой.
 */

/** Сколько ждём установки нового воркера (он качает весь бандл), прежде чем перезагрузиться как есть. */
const INSTALL_TIMEOUT_MS = 20_000;
/** Сколько ждём, пока активированный воркер возьмёт вкладку под управление. */
const CONTROL_TIMEOUT_MS = 5_000;

/** Сообщение, на которое src/sw.ts вызывает skipWaiting. */
const SKIP_WAITING_MESSAGE = { type: 'SKIP_WAITING' } as const;

function withTimeout(promise: Promise<void>, ms: number): Promise<void> {
  return Promise.race([promise, new Promise<void>((resolve) => setTimeout(resolve, ms))]);
}

async function getRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!('serviceWorker' in navigator)) return undefined;
  try {
    return await navigator.serviceWorker.getRegistration();
  } catch {
    return undefined;
  }
}

function waitUntilInstalled(worker: ServiceWorker): Promise<void> {
  return new Promise((resolve) => {
    const onChange = () => {
      if (worker.state === 'installing') return;
      worker.removeEventListener('statechange', onChange);
      resolve();
    };
    worker.addEventListener('statechange', onChange);
    onChange();
  });
}

function waitForControllerChange(): Promise<void> {
  return new Promise((resolve) => {
    navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), { once: true });
  });
}

/**
 * Просит браузер проверить sw.js сейчас, не дожидаясь навигации: новый воркер скачает бандл в фоне и
 * встанет в ожидание. Вызывается, как только version.json сообщил о новой версии, — к нажатию
 * «Обновить» всё уже в кэше. Управление вкладкой воркер не берёт, пока его не попросят.
 */
export async function prefetchServiceWorkerUpdate(): Promise<void> {
  const registration = await getRegistration();
  await registration?.update().catch(() => undefined);
}

/**
 * Перезагружает вкладку на последнюю выложенную сборку: дожидается нового воркера, отдаёт ему управление
 * и перезагружает. Без воркера (dev без PWA, браузер без поддержки) или по таймауту — просто перезагружает.
 */
export async function reloadToLatestVersion(): Promise<void> {
  try {
    const registration = await getRegistration();
    if (registration) {
      await registration.update().catch(() => undefined);
      if (registration.installing) {
        await withTimeout(waitUntilInstalled(registration.installing), INSTALL_TIMEOUT_MS);
      }
      const waiting = registration.waiting;
      if (waiting) {
        const controlled = waitForControllerChange();
        waiting.postMessage(SKIP_WAITING_MESSAGE);
        await withTimeout(controlled, CONTROL_TIMEOUT_MS);
      }
    }
  } finally {
    window.location.reload();
  }
}
